import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, ShieldCheck, MessageCircle, ChevronDown, Globe2 } from "lucide-react";
import Logo from "./Logo";
import { SERVICES } from "../data/services";
import { STATES } from "../data/states";
import { INTL_COUNTRIES } from "../data/intl";
import { FOCUSED_PAGES } from "../data/focusedPages";
import { FLAGS } from "./Flags";
import { SITE } from "../lib/site";

// Collapsible "Explore All Services & Locations" — content stays mounted in the DOM
// (hidden visually when collapsed) so every link remains crawlable.
function ExploreAll() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("services");
  const [counties, setCounties] = useState(null);

  const loadCounties = () => {
    if (counties) return;
    fetch("/data/counties.json")
      .then((r) => r.json())
      .then(setCounties)
      .catch(() => setCounties({}));
  };

  const TABS = [
    ["services", `Services (${SERVICES.length + FOCUSED_PAGES.length})`],
    ["states", `US States (${STATES.length})`],
    ["international", "International"],
    ["counties", "Counties"],
  ];

  return (
    <div className="mt-12 pt-8 border-t border-navy-light" data-testid="footer-explore">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid="footer-explore-toggle"
        className="w-full flex items-center justify-between gap-4 text-left group"
      >
        <span className="inline-flex items-center gap-3">
          <Globe2 className="h-4 w-4 text-aqua" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-aqua">Explore All Services &amp; Locations</span>
          <span className="hidden sm:inline text-xs text-white/40">every service, state, city and county page</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-aqua transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Kept mounted: hidden via class only, links remain in DOM */}
      <div className={open ? "mt-6" : "hidden"}>
        <div className="flex flex-wrap gap-2 border-b border-navy-light pb-4" role="tablist" aria-label="Footer sitemap tabs">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              data-testid={`footer-explore-tab-${key}`}
              onClick={() => { setTab(key); if (key === "counties") loadCounties(); }}
              className={`text-xs font-semibold px-3.5 py-2 rounded-full transition-colors ${
                tab === key ? "bg-aqua text-navy" : "bg-navy-light/40 text-white/60 hover:text-aqua"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6 max-h-80 overflow-y-auto pr-2" data-testid="footer-explore-panel">
          {tab === "services" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2">
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/${s.slug}`} data-testid={`footer-explore-service-${s.slug}`} className="text-xs text-white/60 hover:text-aqua transition-colors py-0.5">
                  {s.navLabel}
                </Link>
              ))}
              {FOCUSED_PAGES.map((p) => (
                <Link key={p.slug} to={`/${p.slug}`} data-testid={`footer-explore-focused-${p.slug}`} className="text-xs text-aqua/80 hover:text-aqua transition-colors py-0.5">
                  {p.keyword}
                </Link>
              ))}
            </div>
          )}

          {tab === "states" && (
            <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-2">
              {STATES.map((st) => (
                <Link key={st.slug} to={`/service-areas/${st.slug}`} data-testid={`footer-explore-state-${st.slug}`} className="text-xs text-white/60 hover:text-aqua transition-colors py-0.5">
                  {st.name}
                </Link>
              ))}
            </div>
          )}

          {tab === "international" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {INTL_COUNTRIES.map((c) => (
                <div key={c.slug}>
                  <Link to={`/${c.slug}`} data-testid={`footer-explore-country-${c.slug}`} className="inline-flex items-center gap-2 text-xs font-bold text-aqua hover:text-aqua-deep transition-colors">
                    {FLAGS[c.slug]} {c.name}
                  </Link>
                  <div className="mt-2.5 space-y-1.5">
                    {c.cities.map((ct) => (
                      <Link key={ct.slug} to={`/${c.slug}/city/${ct.slug}`} data-testid={`footer-explore-city-${ct.slug}`} className="block text-xs text-white/60 hover:text-aqua transition-colors">
                        {ct.name}
                      </Link>
                    ))}
                    {c.regions.map((r) => (
                      <Link key={r.slug} to={`/${c.slug}/region/${r.slug}`} data-testid={`footer-explore-region-${r.slug}`} className="block text-xs text-white/45 hover:text-aqua transition-colors">
                        {r.name}
                      </Link>
                    ))}
                    {c.slug === "canada" && (
                      <Link to="/canada/montreal/fr" data-testid="footer-explore-montreal-fr" className="block text-xs text-aqua/70 hover:text-aqua transition-colors">
                        Montréal (français)
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "counties" && (
            <div>
              {!counties ? (
                <p className="text-xs text-white/50" data-testid="footer-explore-counties-loading">Loading county directory…</p>
              ) : (
                <div className="space-y-5">
                  {STATES.filter((st) => counties[st.slug]?.length).map((st) => (
                    <div key={st.slug}>
                      <Link to={`/service-areas/${st.slug}`} className="text-xs font-bold text-aqua hover:text-aqua-deep transition-colors">
                        {st.name} ({counties[st.slug].length})
                      </Link>
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                        {counties[st.slug].map((c) => (
                          <Link key={c.s} to={`/service-areas/${st.slug}/county/${c.s}`} data-testid={`footer-explore-county-${c.s}`} className="text-[11px] text-white/45 hover:text-aqua transition-colors">
                            {c.n}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-navy-deep text-white relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-contours-dark pointer-events-none" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 relative">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo variant="light" />
            <p className="mt-5 text-sm leading-relaxed text-white/70 max-w-xs">
              A technical production partner for land surveying professionals — preparing detailed CAD drawings and organized GIS deliverables from supplied project data.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 border border-navy-light rounded-sm px-3 py-2">
              <ShieldCheck className="h-4 w-4 text-aqua" />
              <span className="font-mono text-[11px] tracking-wider text-white/70 uppercase">Confidential project handling</span>
            </div>
            <div className="mt-8" data-testid="footer-standards">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Drafted to your standards</p>
              <div className="flex flex-wrap gap-1.5 max-w-xs">
                {["ALTA/NSPS", "NAD83 · State Plane", "PLSS & Metes-Bounds", "County Plat Formats", "Your Templates"].map((s) => (
                  <span key={s} className="font-mono text-[9px] tracking-wider text-white/55 border border-navy-light rounded-sm px-2 py-1">
                    {s}
                  </span>
                ))}
              </div>
              <p className="mt-5 font-mono text-[10px] leading-relaxed tracking-wider text-white/35 max-w-xs">
                AutoCAD · Civil 3D · Carlson · MicroStation · ArcGIS · QGIS · DWG · DXF · DGN · SHP · GeoJSON · LAS
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-aqua mb-5">Services</h3>
            <ul className="space-y-3">
              {SERVICES.slice(0, 7).map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-sm text-white/70 hover:text-aqua transition-colors">
                    {s.navLabel}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" data-testid="footer-all-services" className="text-sm font-semibold text-aqua hover:text-aqua-deep transition-colors">
                  All 22 Services →
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-aqua mb-5">Company</h3>
            <ul className="space-y-3">
              {[
                ["About Us", "/about", "footer-about"],
                ["Our Process", "/our-process", "footer-process"],
                ["Industries", "/industries", "footer-industries"],
                ["Blog", "/blog", "footer-blog"],
                ["Contact", "/contact", "footer-contact"],
                ["Request a Quote", "/request-a-quote", "footer-quote"],
              ].map(([label, to, id]) => (
                <li key={to}>
                  <Link to={to} data-testid={id} className="text-sm text-white/70 hover:text-aqua transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-aqua mt-8 mb-5">Coverage</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/service-areas" data-testid="footer-service-areas" className="text-sm text-white/70 hover:text-aqua transition-colors">
                  Service Areas — All 50 States
                </Link>
              </li>
            </ul>

          </div>

          <div className="lg:col-span-3">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-aqua mb-5">Contact</h3>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="footer-email"
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-aqua transition-colors"
            >
              <Mail className="h-4 w-4 text-aqua" />
              {SITE.email}
            </a>
            <a
              href={SITE.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="mt-3 inline-flex items-center gap-2 text-sm text-white/80 hover:text-aqua transition-colors"
            >
              <MessageCircle className="h-4 w-4 text-aqua" />
              WhatsApp {SITE.whatsappNumber}
            </a>
            <div className="mt-6">
              <Link
                to="/request-a-quote"
                data-testid="footer-cta-quote"
                className="inline-flex items-center gap-2 bg-aqua text-navy font-bold text-sm px-5 py-2.5 rounded-md hover:bg-aqua-deep transition-colors"
              >
                Request a Quote
              </Link>
            </div>
            <div className="mt-8 space-y-3" data-testid="footer-contact-notes">
              <p className="text-xs text-white/55 leading-relaxed">Replies within one business day — usually faster.</p>
              <p className="text-xs text-white/55 leading-relaxed">Chat bubble on every page: messages land straight in our inbox.</p>
              <Link to="/contact" data-testid="footer-support-link" className="inline-block text-xs font-semibold text-aqua hover:text-aqua-deep transition-colors">
                Customer support form →
              </Link>
              <Link to="/pricing" data-testid="footer-pricing-link" className="block text-xs font-semibold text-aqua hover:text-aqua-deep transition-colors">
                See pricing models →
              </Link>
            </div>
          </div>
        </div>

        {/* Regions — single full-width flagged line */}
        <div className="mt-12 pt-7 border-t border-navy-light flex items-center whitespace-nowrap" data-testid="footer-regions">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-aqua mr-4 shrink-0">Regions Served</span>
          <div className="flex items-center overflow-x-auto">
            {[
              ["usa", "USA", "/land-survey-cad-drafting-usa"],
              ["canada", "Canada", "/land-survey-cad-drafting-canada"],
              ["uk", "UK", "/land-survey-cad-drafting-uk"],
              ["australia", "Australia", "/land-survey-cad-drafting-australia"],
            ].map(([key, label, to], i) => (
              <span key={key} className="inline-flex items-center shrink-0">
                {i > 0 && <span className="mx-2.5 text-white/25" aria-hidden="true">|</span>}
                <Link to={to} data-testid={`footer-region-${key}`} className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-aqua transition-colors">
                  {FLAGS[key]}
                  {label}
                </Link>
              </span>
            ))}
          </div>
        </div>

        <ExploreAll />

        {/* Keyword strip — fills the band above the legal bar */}
        <div className="mt-14 pt-8 border-t border-navy-light" data-testid="footer-keyword-strip">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Survey drafting, end to end</p>
          <p className="text-sm leading-loose text-white/45">
            Boundary Surveys · Topographic Drafting · ALTA/NSPS Title Surveys · As-Built Drawings · Subdivision Plats · Easement Exhibits · Construction Staking Plans · Utility Mapping · Route & ROW Sheets · Drone & Point Cloud Drafting · FEMA Elevation Certificates · Cadastral Mapping · Plot Plans · Grading & Earthwork · Road & Highway Sheets · PDF to DWG · Point Cloud to CAD · Survey Data Processing · GIS Mapping · GIS Data Conversion
          </p>
        </div>

        <div className="mt-14 pt-6 border-t border-navy-light flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-white/50" data-testid="footer-copyright">
            © {new Date().getFullYear()} GeoDrafts. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="text-xs text-white/50 hover:text-aqua transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" data-testid="footer-terms" className="text-xs text-white/50 hover:text-aqua transition-colors">
              Terms and Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
